// /factories/constants.mjs

// Canonical face names for geometry and attachments
export const FACE_NAMES = Object.freeze(['front', 'rear', 'left', 'right', 'top', 'bottom']);

// Segment types for body geometry
export const SEGMENT_TYPES = Object.freeze(['main', 'step', 'drawbar', 'cab', 'deck']);

// Coupling types (included for reference; may be unused if attachment names suffice)
export const COUPLING_TYPES = Object.freeze([
   'A', // Drawbar/eye
   'B', // Kingpin/turntable
]);

// Standard attachment point types
export const ATTACHMENT_TYPES = Object.freeze([
   'kingpin', // B-coupling
   'pinCoupling', // A-coupling (rigid/pig/dog trailer rear of truck)
   'fifthWheel', // B-coupling (on prime mover)
   'drawbarEye', // A-coupling (front of trailers/dollies)
]);

// Standard heavy vehicle dimensional defaults (in meters)
const defaultDrawbarAttachmentY = 0.9;
const defaultDrawbarUnderrun = 0; // Default coupling underrun distance [m]
const defaultFifthWheelY = 1.3; // Example, fifth wheel/platform height above ground [m]

export const DEFAULTS = {
   // Visualisation constants
   drawbarHeight: 0.08, // height and width of physical drawbar
   drawbarWidth: 0.08,
   chassisHeight: 0.2, // height of chassis
   chassisGap: 0.1, // gap between cab/cargo and chassis
   cabGHeight: 2.9,
   cabGap: 0.1, // gap between cab and cargo
   cabLength: 2.0, // length of cab

   // Prime mover dimensions
   primeMover: {
      bodyWidth: 2.3,
      bodyLength: 4.9,

      cabGHeight: 3.0,
      cabFifthWheel: 2.4, // Distance from rear of cab to fifth wheel
      turntableGHeight: defaultFifthWheelY, // Height of turntable above ground
   },

   // semi-trailer dimensions
   semiTrailer: {
      bodyWidth: 2.5,
      kingpinGHeight: defaultFifthWheelY, // Kingpin above ground [m]
      pinCouplingHeight: defaultDrawbarAttachmentY,
      pinCouplingUnderrun: defaultDrawbarUnderrun, // Default coupling underrun distance [m]
      bodyLength: 13.3,
      bodyGHeight: 3.6,
      forwardOverhang: 1.5, // Distance from front of trailer to kingpin
   },

   // lead-semi-trailer dimensions
   leadSemiTrailer: {
      bodyGHeight: 3.6,
      bodyLength: 13.3,

      forwardOverhang: 1.5, // Distance from front of trailer to kingpin
      kingpinGHeight: defaultFifthWheelY, // Kingpin above ground [m]
      turntableGHeight: defaultFifthWheelY, // Height of turntable above ground
      turntableRearClearance: 2.4, // Distance from rear of body to turntable (rear clearance)

      bodyWidth: 2.5,
   },

   // Rigid truck dimensions
   rigidTruck: {
      bodyWidth: 2.2,
      bodyLength: 7.1, // overall length
      cargoGHeight: 2.8, // Height of the cargo from ground to top
      pinCouplingGHeight: defaultDrawbarAttachmentY, // Height of pin coupling from ground
      pinCouplingUnderrun: defaultDrawbarUnderrun,
   },

   // Dog trailer dimensions
   dogTrailer: {
      drawbarBaseHeight: defaultDrawbarAttachmentY, // Height of the drawbar base from ground to bottom
      drawbarLength: 3.55, // Length of the drawbar from the trailer to the truck

      bodyGHeight: 2.4, // Height of the trailer body from ground to top
      forwardOverhang: 0.7, // Forward overhang of the trailer body from the drawbar pivot point
      bodyWidth: 2.5,
      bodyLength: 6.5,

      pinCouplingHeight: defaultDrawbarAttachmentY, // Height of the pin coupling from ground
      pinCouplingUnderrun: defaultDrawbarUnderrun,
   },
   pigTrailer: {
      drawbarBaseHeight: defaultDrawbarAttachmentY, // Height of the drawbar base from ground to bottom
      drawbarLength: 1, // Length of the drawbar from the trailer to the truck

      bodyWidth: 2.5,
      bodyLength: 4.5,
      bodyGHeight: 2.4, // Height of the trailer body from ground to top
   },
   converterDolly: {
      drawbarBaseHeight: defaultDrawbarAttachmentY, // Height of the drawbar base from ground to bottom
      drawbarLength: 2.5, // Length of the drawbar from the trailer to the truck

      //turntableGHeight: defaultFifthWheelY, // Height of the turntable above ground
      bodyWidth: 2.4,
   },
};

export const unitClearanceDefaults = {
   'prime-mover': {
      ...DEFAULTS.primeMover,
   },

   'semi-trailer': {
      ...DEFAULTS.semiTrailer,
   },

   'lead-semi-trailer': {
      ...DEFAULTS.leadSemiTrailer,
   },

   'rigid-truck': {
      ...DEFAULTS.rigidTruck,
   },

   'dog-trailer': {
      ...DEFAULTS.dogTrailer,
   },

   'pig-trailer': {
      ...DEFAULTS.pigTrailer,
   },

   'converter-dolly': {
      ...DEFAULTS.converterDolly,
   },
};

// Specific roll limits for different coupling types
export const ROLL = {
   drawbarEye: 15, // A-coupling (drawbar) following trailer roll limit
   kingpin: 4, // B-coupling (kingpin), kingpin can allow minimal roll if enabled
};

export function autoUnitId(type) {
   return `${type}-${Date.now()}`;
}
